import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Select,
  Row,
  Col,
  Tag,
  Card,
  Spin,
  Flex,
  Badge,
} from "antd";
import dayjs from "dayjs";
import { getAllActiveUsers } from "../../../apis/userManagementService";

interface Props {
  open: boolean;
  onClose: () => void;
  availableHospitals: { label: string; value: string | number }[];
  selectedHospital: number | null;
  selectedScheme: number | null;
  isViewMoreModalDataLoading: boolean;
  viewMoreModalData: any[];
  fetchViewMoreModalData: (payload: any) => void;
  viewMoreDataTotal: number;
}

const ViewMoreModal: React.FC<Props> = ({
  open,
  onClose,
  availableHospitals,
  selectedHospital,
  selectedScheme,
  isViewMoreModalDataLoading,
  viewMoreModalData,
  fetchViewMoreModalData,
  viewMoreDataTotal,
}) => {
  const [modalSelectedHospital, setModalSelectedHospital] = useState<
    string | number
  >(selectedHospital || "");
  const [selectedRegisterType, setSelectedRegisterType] = useState<any>("ALL");
  const [selectedUpdatedBy, setSelectedUpdatedBy] = useState<any>(-1);
  const [selectedDateRangeType, setSelectedDateRangeType] =
    useState<any>("last30Days");
  const [selectedStatus, setSelectedStatus] = useState<any>("ALL");
  const [selectedProcess, setSelectedProcess] = useState<any>("ALL");
  const [users, setUsers] = useState<any>([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    if (!open) {
      setPagination({
        current: 1,
        pageSize: 10,
      });

      setSelectedRegisterType("ALL");
      setSelectedUpdatedBy(-1);
      setSelectedDateRangeType("last30Days");
      setSelectedStatus("ALL");
      setSelectedProcess("ALL");
    }

    if (open) {
      setModalSelectedHospital(selectedHospital || '');
      fetchAllActiveUsers();
      getModalData(true);
    }
  }, [open]);

  const fetchAllActiveUsers = async () => {
    try {
      const resp = await getAllActiveUsers("");
      setUsers(resp);
    } catch (error) {
      console.error("Error fetching active users:", error);
      setUsers([]);
    }
  };

  const calculateDateRange = (type: string) => {
    let startDate;
    let endDate;

    switch (type) {
      case "today":
        startDate = dayjs().startOf("day");
        endDate = dayjs().endOf("day");
        break;

      case "last7Days":
        startDate = dayjs().subtract(6, "day").startOf("day"); // including today
        endDate = dayjs().endOf("day");
        break;

      case "last30Days":
        startDate = dayjs().subtract(29, "day").startOf("day"); // including today
        endDate = dayjs().endOf("day");
        break;

      default:
        startDate = null;
        endDate = null;
    }

    return {
      startDate: startDate ? startDate.format("YYYY-MM-DDTHH:mm:ss.SSS") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DDTHH:mm:ss.SSS") : null,
    };
  };

  useEffect(() => {
    if (open) {
      getModalData(false);
    }
  }, [
    modalSelectedHospital,
    selectedRegisterType,
    selectedUpdatedBy,
    selectedDateRangeType,
    selectedStatus,
    selectedProcess,
  ]);

  const getModalData = (
    isInitialApiCall: boolean,
    page: number = 1,
    size: number = 10,
  ) => {
    const { startDate, endDate } = calculateDateRange(
      isInitialApiCall ? "last30Days" : selectedDateRangeType,
    );
    fetchViewMoreModalData({
      hospitalId: isInitialApiCall ? selectedHospital : modalSelectedHospital,
      eodBodProcessType: isInitialApiCall ? "ALL" : selectedProcess,
      status: isInitialApiCall ? "ALL" : selectedStatus,
      performedBy: isInitialApiCall ? -1 : selectedUpdatedBy,
      startDate,
      endDate,
      page: page - 1,
      size,
    });
  };

  const onPaginationChange = (page: number, size: number) => {
    getModalData(false, page, size);
  };

  const handleHospitalChange = (value: string | number) => {
    setModalSelectedHospital(value);
  };

  const handleRegisterTypeChange = (value: string | number) => {
    setSelectedRegisterType(value);
  };

  const handleUpdatedByChange = (id: number) => {
    setSelectedUpdatedBy(id);
  };

  const handleDateChange = (value: string | number) => {
    setSelectedDateRangeType(value);
  };

  const handleStatusChange = (value: string | number) => {
    setSelectedStatus(value);
  };

  const handleProcessChange = (value: string | number) => {
    setSelectedProcess(value);
  };

  const renderValue = (value: any) =>
    value === null || value === undefined || value === "" ? "-" : value;

  const columns = [
    {
      title: <strong>Process Type</strong>,
      dataIndex: "eodBodProcessType",
      render: renderValue,
    },
    {
      title: <strong>Hospital Name</strong>,
      dataIndex: "hospitalName",
      render: (text: string) => <strong>{renderValue(text)}</strong>,
    },
    {
      title: <strong>Updated By</strong>,
      dataIndex: "performedByName",
      render: (text: string) => <strong>{renderValue(text)}</strong>,
    },
    {
      title: <strong>Timestamp</strong>,
      dataIndex: "executedAt",
      render: (_: any, record: any) => {
        return record?.executedAt
          ? dayjs.utc(record.executedAt).local().format("DD-MM-YYYY hh:mm A")
          : "-";
      },
    },
    {
      title: <strong>Status</strong>,
      dataIndex: "status",
      render: (status: string) => (
        <strong>
          <Tag
            color={status?.toLowerCase() === "success" ? "green" : "orange"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              borderRadius: "999px",
              padding: "2px 12px",
            }}
          >
            <Badge
              color={status?.toLowerCase() === "success" ? "green" : "orange"}
            />
            {status ?? "-"}
          </Tag>
        </strong>
      ),
    },
  ];

  const SCALE = 0.67;
    
      const [scrollY, setScrollY] = useState(window.innerHeight / SCALE - 210);
    
      useEffect(() => {
        const handleResize = () => {
          setScrollY(window.innerHeight / SCALE - 210);
        };
    
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1550}
      title="Process History :"

      wrapClassName="management-age-stage-table-modal"

      bodyStyle={{
        height: "calc(144vh - 55px)",
        overflow: "auto",
        padding: 0,
      }}

      style={{
        top: 10,
        left: 4,
        padding: 0,
        maxWidth: "100vw",
        height: "145vh",
      }}
    >
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: "#fafafa",
          border: "1px solid #e5e5e5",
          padding: 12,
        }}
      >
        <Row gutter={16}>
          <Col flex="1">
            <strong>Hospital</strong>
            <Select
              showSearch
              placeholder="Select a Hospital"
              optionFilterProp="label"
              style={{ width: "100%", marginTop: 5 }}
              value={modalSelectedHospital}
              options={availableHospitals}
              onChange={handleHospitalChange}
            />
          </Col>

          <Col flex="1">
            <strong>Updated By</strong>
            <Select
              showSearch
              optionFilterProp="label"
              value={selectedUpdatedBy}
              style={{ width: "100%", marginTop: 5 }}
              options={
                users?.length
                  ? [
                      { label: "ALL", value: -1 },
                      ...users.map((user: any) => ({
                        label: `${user.firstname} ${user.lastname}`,
                        value: user.id,
                      })),
                    ]
                  : [{ label: "ALL", value: -1 }]
              }
              onChange={handleUpdatedByChange}
              allowClear
            />
          </Col>

          <Col flex="1">
            <strong>Date Range</strong>
            <Select
              value={selectedDateRangeType}
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "Last 30 Days", value: "last30Days" },
                { label: "Last 7 Days", value: "last7Days" },
                { label: "Today", value: "today" },
              ]}
              onChange={handleDateChange}
            />
          </Col>

          <Col flex="1">
            <strong>Status</strong>
            <Select
              value={selectedStatus}
              placeholder="Select"
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "ALL", value: "ALL" },
                { label: "PROCESSING", value: "PROCESSING" },
                { label: "RUNNING", value: "RUNNING" },
                { label: "SUCCESS", value: "SUCCESS" },
                { label: "FAILED", value: "FAILED" },
              ]}
              onChange={handleStatusChange}
            />
          </Col>

          <Col flex="1">
            <strong>Process</strong>
            <Select
              value={selectedProcess}
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "ALL", value: "ALL" },
                { label: "EOD", value: "EOD" },
                { label: "BOD", value: "BOD" },
              ]}
              onChange={handleProcessChange}
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <Spin spinning={isViewMoreModalDataLoading}>
          <Table
            columns={columns}
            dataSource={viewMoreModalData}
            rowKey={(record: any, index) => record?.id || index}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: viewMoreDataTotal,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
                const payload = {
                  hospitalId: modalSelectedHospital,
                  fetchType: selectedProcess,
                  status: selectedStatus,
                  performedBy: selectedUpdatedBy,
                  page,
                  size: pageSize,
                };
                onPaginationChange(page, pageSize);
              },
            }}
            scroll={{
            x: 150,
            y: scrollY,
          }}
            size="middle"
            style={{ maxHeight: "500px" }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "ant-table-row-light" : ""
            }
          />
        </Spin>
      </Card>
    </Modal>
  );
};

export default ViewMoreModal;
